import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { TicketCard } from "@/components/TicketCard";
import {
  CheckCircle, Wallet, ShoppingBag, Smartphone, Mail,
  AlertCircle, Copy, Ticket
} from "lucide-react";

export default function TicketSuccessPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const sessionId = searchParams.get("session_id");
  const orderIdParam = searchParams.get("order_id");
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const [order, setOrder] = useState(null);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    if (orderIdParam) { loadFreeOrder(); return; }
    if (sessionId) { pollPaidStatus(); return; }
    setStatus("error");
  }, []);

  const loadFreeOrder = async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderIdParam}`, { withCredentials: true });
      setOrder(res.data.order);
      setTickets(res.data.tickets || []);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const pollPaidStatus = async () => {
    const maxAttempts = 8;
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        if (res.data.payment_status === "paid") {
          setOrder(res.data.order);
          setTickets(res.data.tickets || []);
          setStatus("success");
          return;
        }
        if (res.data.status === "expired") { setStatus("expired"); return; }
      } catch {}
      await new Promise(r => setTimeout(r, 2000));
      setAttempts(i + 1);
    }
    setStatus("timeout");
  };

  const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

  return (
    <Layout>
      <section className="section-spacing" data-testid="ticket-success-page">
        <div className="container-custom max-w-3xl mx-auto">
          {status === "checking" && (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-deep-blue mx-auto mb-4" />
              <h2 className="text-xl font-bold text-deep-blue">Loading Your Tickets...</h2>
              {sessionId && <p className="text-slate-500 mt-2">Attempt {attempts + 1} of 8</p>}
            </div>
          )}

          {status === "success" && (
            <>
              <div className="text-center mb-8">
                <div className="inline-flex w-20 h-20 rounded-full bg-green-100 items-center justify-center mb-4">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
                <h1 className="text-3xl font-bold text-deep-blue mb-2">Tickets Confirmed!</h1>
                {user ? (
                  <p className="text-slate-600">Thank you, {user.name || "valued supporter"}. Your tickets are ready — open <Link to="/my-wallet" className="text-deep-blue font-semibold underline">My Wallet</Link> to view them anytime.</p>
                ) : (
                  <p className="text-slate-600">Thank you{order?.customer_name ? `, ${order.customer_name}` : ""}. Your tickets are ready — <strong>download or print them below</strong>, and we've also emailed a copy to{order?.customer_email ? <> <span className="font-semibold">{order.customer_email}</span></> : " your email"}.</p>
                )}
              </div>

              {order && (
                <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6 shadow-sm" data-testid="order-summary">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Order Number</p>
                      <p className="font-mono text-deep-blue font-bold text-lg" data-testid="order-number">{order.order_id}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Total Paid</p>
                      <p className="font-bold text-deep-blue text-lg">{fmtMoney(order.total)}</p>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 border-t pt-3">
                    {order.payment_method === "free" ? "Free reservation" : "Paid via Stripe"} • {tickets.length} ticket{tickets.length !== 1 ? "s" : ""}
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6" data-testid="ticket-instructions">
                {user ? (
                  <>
                    <h3 className="font-bold text-deep-blue mb-3 flex items-center gap-2"><Wallet className="w-5 h-5" /> Your tickets are in My Wallet</h3>
                    <ol className="space-y-2 text-sm text-slate-700">
                      <li><strong>1. Open <Link to="/my-wallet" className="text-blue-700 underline font-semibold">My Wallet</Link></strong> anytime to see all your tickets in one place.</li>
                      <li><strong>2. Download or print</strong> each ticket using the buttons below, OR keep them in your wallet on your phone.</li>
                      <li><strong>3. At the event:</strong> Show the QR code (from your wallet or printed) at the entrance — no signal needed once saved.</li>
                      <li><strong>4. On the go?</strong> Scan the Mobile Handoff QR below to open My Wallet instantly on your phone.</li>
                    </ol>
                    <div className="mt-4 flex items-start gap-2 bg-green-50 border border-green-200 rounded p-3 text-xs text-green-900">
                      <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span><strong>No need to wait for an email.</strong> Your tickets are saved in your wallet and ready to use right now — open <Link to="/my-wallet" className="font-semibold underline">My Wallet</Link> anytime.</span>
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="font-bold text-deep-blue mb-3 flex items-center gap-2"><Ticket className="w-5 h-5" /> Save your tickets right now</h3>
                    <ol className="space-y-2 text-sm text-slate-700">
                      <li><strong>1. Download or print</strong> each ticket below — save them to your phone's photos or Files.</li>
                      <li><strong>2. Check your email</strong> — we sent a copy of every ticket to {order?.customer_email || "the address you provided"}.</li>
                      <li><strong>3. At the event:</strong> Show the QR code (from your phone or printed) at the entrance — no signal needed once saved.</li>
                      <li><strong>4. Lost your email?</strong> <Link to="/tickets/retrieve" className="text-blue-700 underline font-semibold" data-testid="retrieve-link">Retrieve your tickets by email</Link> anytime.</li>
                    </ol>
                    <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span><strong>Bookmark this page</strong> or save the tickets now — you won't be able to reach this page again without the confirmation link we emailed you.</span>
                    </div>
                  </>
                )}
              </div>

              {/* === Mobile Handoff QR === */}
              <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-deep-blue/20 rounded-xl p-6 mb-6" data-testid="mobile-handoff">
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <div className="bg-white p-4 rounded-xl shadow-md shrink-0" data-testid="mobile-handoff-qr">
                    <QRCodeSVG
                      value={window.location.href}
                      size={180}
                      level="H"
                      includeMargin={false}
                      bgColor="#ffffff"
                      fgColor="#0a1e3d"
                    />
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <div className="inline-flex items-center gap-2 bg-deep-blue text-white text-xs font-bold uppercase tracking-wider px-2 py-1 rounded mb-2">
                      <Smartphone className="w-3 h-3" /> Open on Phone
                    </div>
                    <h3 className="text-lg font-bold text-deep-blue mb-2">Scan to open your tickets on your phone</h3>
                    <ol className="text-sm text-slate-700 space-y-1.5 mb-3">
                      <li><strong>1.</strong> Open your phone's camera app</li>
                      <li><strong>2.</strong> Point it at this QR code (no need to take a photo — it'll detect automatically)</li>
                      <li><strong>3.</strong> Tap the link that appears at the top of your screen</li>
                      <li><strong>4.</strong> Your tickets open instantly on your phone — tap <em>Download</em> on each to save them to your photo library or Files</li>
                    </ol>
                    <p className="text-xs text-slate-500">No app install needed. Works on iPhone and Android.</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(window.location.href);
                          toast.success("Ticket link copied — you can send it to yourself by text or email");
                        } catch {
                          toast.error("Clipboard unavailable — long-press the page URL to copy manually");
                        }
                      }}
                      className="mt-3"
                      data-testid="copy-ticket-link-btn"
                    >
                      <Copy className="w-3.5 h-3.5 mr-2" /> Or copy ticket link
                    </Button>
                  </div>
                </div>
              </div>

              {tickets.length > 0 ? (
                <div className="grid gap-5 mb-6">
                  {tickets.map((t, i) => <TicketCard key={t.ticket_id} ticket={t} idx={i} total={tickets.length} />)}
                </div>
              ) : (
                <div className="bg-slate-50 rounded-xl p-6 text-center mb-6">
                  <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">Your order is confirmed. Tickets are being generated — head to <Link to="/my-wallet" className="text-blue-600 underline">My Wallet</Link> to view them.</p>
                </div>
              )}

              <div className="flex flex-wrap gap-3 justify-center pt-4">
                {user && (
                  <>
                    <Link to="/my-wallet"><Button variant="outline" className="text-deep-blue" data-testid="goto-wallet"><Wallet className="w-4 h-4 mr-2" /> Open My Wallet</Button></Link>
                    <Link to="/dashboard"><Button variant="outline" data-testid="goto-orders"><ShoppingBag className="w-4 h-4 mr-2" /> My Orders</Button></Link>
                  </>
                )}
                <Link to="/events"><Button variant="outline">Browse More Events</Button></Link>
              </div>
            </>
          )}

          {(status === "expired" || status === "timeout" || status === "error") && (
            <div className="text-center py-16">
              <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-deep-blue mb-2">Something Went Wrong</h2>
              <p className="text-slate-500 mb-2 max-w-lg mx-auto">
                {status === "expired" ? "This payment session has expired. If you were not charged, please try again." :
                 status === "timeout" ? (user ? "We can't confirm your order right now. Your tickets are safe in My Wallet — please check there." : "We can't confirm your order right now. Please check your email for the ticket confirmation, or contact us with your order details.") :
                 (user ? "We couldn't load your order. Your tickets are safe — please check My Wallet or My Tickets." : "We couldn't load your order. Please check your email for your ticket confirmation, or contact us for help.")}
              </p>
              <div className="flex flex-wrap gap-3 justify-center mt-6">
                {user && (
                  <>
                    <Link to="/my-wallet"><Button className="bg-deep-blue text-white"><Wallet className="w-4 h-4 mr-2" /> Check My Wallet</Button></Link>
                    <Link to="/dashboard"><Button variant="outline"><ShoppingBag className="w-4 h-4 mr-2" /> My Tickets</Button></Link>
                  </>
                )}
                {!user && (
                  <Link to="/tickets/retrieve"><Button className="bg-deep-blue text-white" data-testid="goto-retrieve"><Ticket className="w-4 h-4 mr-2" /> Retrieve my tickets by email</Button></Link>
                )}
                <Link to="/contact"><Button variant="ghost"><Mail className="w-4 h-4 mr-2" /> Contact Us</Button></Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
